import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { AppAuthFrame, readRememberedTenantCode, rememberTenantCode } from './app-login-page';
import { rememberLoginMode } from '../../shared/login-mode';
import { PIN_LENGTH, PinPad } from './pin-pad';

type Stage = 'code' | 'pin' | 'confirm';

/**
 * Activeren met de code van de brief, en meteen een pincode kiezen.
 *
 * Drie stappen achter elkaar in plaats van één lang formulier: op een telefoon past dat
 * niet onder elkaar, en de pincode twee keer intypen is de enige bescherming tegen een
 * verkeerd onthouden cijfer — daarna is hij niet meer terug te lezen.
 */
export function AppActivatePage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('code');
  const [tenantCode, setTenantCode] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTenantCode(readRememberedTenantCode());
  }, []);

  const finish = async () => {
    setError(null);
    if (pin !== confirmation) {
      setConfirmation('');
      setError(t('activate.pinMismatch'));
      setStage('confirm');
      return;
    }

    const result = await session.activate({
      tenantCode: tenantCode.trim(),
      employeeNumber: employeeNumber.trim(),
      activationCode: activationCode.trim(),
      pin,
    });

    if (result === null) {
      rememberTenantCode(tenantCode.trim());
      // Geactiveerd met een pincode: zo begint het aanmeldscherm de volgende keer (ORG-3).
      rememberLoginMode('pin');
      void navigate({ to: '/app' });
      return;
    }

    // Een afgekeurde pincode is iets anders dan een verkeerde code: bij het eerste mag de
    // medewerker gewoon opnieuw kiezen, bij het tweede moet hij terug naar het begin.
    setPin('');
    setConfirmation('');
    if (result === 'failed') {
      setStage('code');
      setError(t('activate.codeFailed'));
    } else if (result === 'locked') {
      setStage('code');
      setError(t('appLogin.locked'));
    } else {
      setStage('pin');
      setError(result === 'invalid' ? t('activate.pinRejected') : result);
    }
  };

  return (
    <AppAuthFrame title={t('activate.title')} intro={t(`activate.intro.${stage}`)}>
      {stage === 'code' ? (
        <form
          className="flex flex-col gap-element"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            setStage('pin');
          }}
        >
          <Input
            label={t('login.tenantCode')}
            name="tenantCode"
            autoComplete="organization"
            value={tenantCode}
            onChange={(event) => setTenantCode(event.target.value)}
            required
          />
          <Input
            label={t('appLogin.employeeNumber')}
            name="employeeNumber"
            inputMode="numeric"
            value={employeeNumber}
            onChange={(event) => setEmployeeNumber(event.target.value)}
            required
          />
          <Input
            label={t('activate.code')}
            hint={t('activate.codeHint')}
            name="activationCode"
            autoCapitalize="characters"
            value={activationCode}
            onChange={(event) => setActivationCode(event.target.value.toUpperCase())}
            required
          />
          {error ? (
            <p
              role="alert"
              className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger"
            >
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            className="w-full"
            disabled={
              tenantCode.trim() === '' ||
              employeeNumber.trim() === '' ||
              activationCode.replace(/[^A-Za-z0-9]/g, '').length < 10
            }
          >
            {t('common.next')}
          </Button>
        </form>
      ) : null}

      {stage === 'pin' ? (
        <div className="flex flex-col gap-element">
          <PinPad value={pin} onChange={setPin} label={t('activate.choosePin')} />
          {error ? (
            <p
              role="alert"
              className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger"
            >
              {error}
            </p>
          ) : null}
          <Button
            className="w-full"
            disabled={pin.length !== PIN_LENGTH}
            onClick={() => {
              setError(null);
              setStage('confirm');
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      ) : null}

      {stage === 'confirm' ? (
        <div className="flex flex-col gap-element">
          <PinPad value={confirmation} onChange={setConfirmation} label={t('activate.repeatPin')} />
          {error ? (
            <p
              role="alert"
              className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger"
            >
              {error}
            </p>
          ) : null}
          <Button
            className="w-full"
            loading={session.loading}
            disabled={confirmation.length !== PIN_LENGTH}
            onClick={() => void finish()}
          >
            {t('activate.submit')}
          </Button>
        </div>
      ) : null}

      <p className="mt-element text-center text-content text-text-secondary">
        <Link to="/app/login" className="text-brand-primary underline">
          {t('activate.toLogin')}
        </Link>
      </p>
    </AppAuthFrame>
  );
}
